/**
 * Debug Panel Component
 * 
 * Shows intermediate agent results for debugging purposes.
 * Toggle visibility with Ctrl+Shift+D.
 */

import * as React from "react";
import { makeStyles, tokens, Button, Text } from "@fluentui/react-components";
import { ChevronDown20Regular, ChevronRight20Regular, Delete20Regular } from "@fluentui/react-icons";

interface ToolResult {
    id: string;
    timestamp: string;
    agentName: string;
    toolName: string;
    result: unknown;
}

interface DebugPanelState {
    isVisible: boolean;
    results: ToolResult[];
    expandedIds: Set<string>;
}

const useStyles = makeStyles({
    container: {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: "50vh",
        backgroundColor: tokens.colorNeutralBackground1,
        borderTop: `2px solid ${tokens.colorBrandBackground}`,
        boxShadow: tokens.shadow16,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        backgroundColor: tokens.colorNeutralBackground3,
        borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    title: {
        fontWeight: 600,
        fontSize: "12px",
        color: tokens.colorNeutralForeground1,
    },
    headerActions: {
        display: "flex",
        gap: "8px",
    },
    content: {
        flex: 1,
        overflowY: "auto",
        padding: "8px",
        fontFamily: "Consolas, Monaco, monospace",
        fontSize: "11px",
    },
    resultItem: {
        marginBottom: "8px",
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: "4px",
        overflow: "hidden",
    },
    resultHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 8px",
        backgroundColor: tokens.colorNeutralBackground4,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground5,
        },
    },
    resultMeta: {
        display: "flex",
        gap: "12px",
        flex: 1,
        alignItems: "center",
    },
    agentName: {
        color: tokens.colorBrandForeground1,
        fontWeight: 600,
    },
    toolName: {
        color: tokens.colorPalettePurpleForeground2,
    },
    timestamp: {
        color: tokens.colorNeutralForeground3,
        fontSize: "10px",
        marginLeft: "auto",
    },
    resultBody: {
        padding: "8px",
        backgroundColor: tokens.colorNeutralBackground2,
        maxHeight: "200px",
        overflowY: "auto",
    },
    pre: {
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: "11px",
        lineHeight: 1.4,
    },
    empty: {
        textAlign: "center",
        color: tokens.colorNeutralForeground3,
        padding: "24px",
    },
    badge: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        borderRadius: "10px",
        padding: "2px 8px",
        fontSize: "10px",
        fontWeight: 600,
    },
});

// Global store for debug results (persists across re-renders)
let globalDebugState: DebugPanelState = {
    isVisible: false,
    results: [],
    expandedIds: new Set(),
};

// Event emitter for state updates
const listeners = new Set<() => void>();

function notifyListeners() {
    listeners.forEach(fn => fn());
}

// Public API to add results from anywhere
export function addDebugResult(agentName: string, toolName: string, result: unknown) {
    const newResult: ToolResult = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: new Date().toLocaleTimeString(),
        agentName,
        toolName,
        result,
    };
    
    globalDebugState = {
        ...globalDebugState,
        results: [newResult, ...globalDebugState.results].slice(0, 50), // Keep last 50
    };
    
    console.log(`[DebugPanel] Added result from ${agentName}.${toolName}`, result);
    notifyListeners();
}

// Public API to toggle visibility
export function toggleDebugPanel() {
    globalDebugState = {
        ...globalDebugState,
        isVisible: !globalDebugState.isVisible,
    };
    notifyListeners();
}

// Public API to clear results
export function clearDebugResults() {
    globalDebugState = {
        ...globalDebugState,
        results: [],
    };
    notifyListeners();
}

// Public API to check if panel is visible
export function isDebugPanelVisible(): boolean {
    return globalDebugState.isVisible;
}

export const DebugPanel: React.FC = () => {
    const styles = useStyles();
    const [state, setState] = React.useState(globalDebugState);
    const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());

    // Subscribe to global state changes
    React.useEffect(() => {
        const listener = () => setState({ ...globalDebugState });
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    }, []);

    // Keyboard shortcut: Ctrl+Shift+D
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                e.preventDefault();
                toggleDebugPanel();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const toggleExpanded = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    if (!state.isVisible) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text className={styles.title}>🔍 Debug Panel</Text>
                    <span className={styles.badge}>{state.results.length}</span>
                </div>
                <div className={styles.headerActions}>
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<Delete20Regular />}
                        onClick={clearDebugResults}
                    >
                        Clear
                    </Button>
                    <Button
                        appearance="subtle"
                        size="small"
                        onClick={toggleDebugPanel}
                    >
                        Close
                    </Button>
                </div>
            </div>
            <div className={styles.content}>
                {state.results.length === 0 ? (
                    <div className={styles.empty}>
                        No results yet. Send a message that triggers ImageSearchAgent.
                    </div>
                ) : (
                    state.results.map(item => {
                        const isExpanded = expandedIds.has(item.id);
                        return (
                            <div key={item.id} className={styles.resultItem}>
                                <div
                                    className={styles.resultHeader}
                                    onClick={() => toggleExpanded(item.id)}
                                >
                                    {isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                                    <div className={styles.resultMeta}>
                                        <span className={styles.agentName}>{item.agentName}</span>
                                        <span className={styles.toolName}>{item.toolName}</span>
                                        <span className={styles.timestamp}>{item.timestamp}</span>
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div className={styles.resultBody}>
                                        <pre className={styles.pre}>
                                            {JSON.stringify(item.result, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default DebugPanel;

